import React, { useEffect, useState } from 'react';
import { EmailCaptureFormContentfulModel } from 'src/api/models/EmailCaptureForm';
import {
  HubspotArticleEmailCaptureContainer,
  HubspotArticleEmailCaptureLeftBackground,
  HubspotArticleEmailCaptureRightContent,
  HubspotArticleEmailCtaText,
  HubspotArticleEmailDescriptionText,
  HubspotArticleEmailTextContainer,
  HubspotFormWrapper,
  LeftBackgroundImage,
  LeftBackgroundText,
} from './HubspotEmailCapture.styles';
import { createHubSpotForm, submitHubSpotFormBuilder } from 'src/functions/hubspot/hubspot.service';
import TextureOverlay from 'src/molecules/Standard/Shared/TextureOverlay/TextureOverlay';
import { palette } from 'src/styles/colors';
import WaterTextureOverlay from 'src/molecules/Standard/Shared/TextureOverlay/WaterTextureOverlay';
import Heading from 'src/elements/typography/heading';
import IconsPartyHat from 'src/images/IconsPartyHat';
import { useFormResultController } from 'src/hooks/useFormResultController';
import ButtonPrimary from 'src/elements/buttons/2023_Q4/ButtonPrimary';
import HubspotFormResult from './HubspotEmailCaptureFormResult';
import { formatElementFromHtmlAst } from 'src/functions/formatElement.htmlAst';

const HUBSPOT_ELEM = 'hubspotArticleForm';
const submitHubSpotForm = submitHubSpotFormBuilder(`#${HUBSPOT_ELEM}`);

const HubspotArticleEmailCapture = (props: EmailCaptureFormContentfulModel): JSX.Element => {
  const [disabled, setDisabled] = useState(false);
  const [formResult, setFormResult] = useState<null | 'failure' | 'success'>(null);
  const { isEmailSubscriptionSubmitted, setFormSubmission } = useFormResultController();

  useEffect(() => {
    if (isEmailSubscriptionSubmitted()) {
      setFormResult('success');
      return;
    }

    // Form ID for Email Capture Footer Subscriber
    const formId = props.formId;
    createHubSpotForm({
      formContainer: `#${HUBSPOT_ELEM}`,
      formId,
      onFormReadyCallback: () => {
        const slugInput = document.querySelector<HTMLInputElement>(
          `#${HUBSPOT_ELEM} input[name="form_submittal_page"]`
        );
        const emailInput = document.querySelector<HTMLInputElement>(`#${HUBSPOT_ELEM} input[name="email"]`); // Hubspot email input field

        emailInput.setAttribute('placeholder', '');
        slugInput.value = window.location.pathname;
      },
      onFormSubmitCallback: () => {
        setDisabled(true);
      },
      onFormSubmittedCallback: () => {
        setFormResult('success');
        setFormSubmission();
      },
      errorCallback: () => {
        const failureTimeout = setTimeout(() => {
          setFormResult('failure');
          setDisabled(true);
          clearTimeout(failureTimeout);
        }, 2000);
      },
    });
  }, [isEmailSubscriptionSubmitted]);

  const handleSubmit = () => {
    // Focus is needed to "activate" the input value update in Hubspot form
    const emailInput = document.querySelector<HTMLInputElement>(`#${HUBSPOT_ELEM} input[name="email"]`); // Hubspot email input field
    const isValidEmail = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(emailInput.value);
    if (!isValidEmail) {
      return;
    }
    submitHubSpotForm();
  };

  return (
    <HubspotArticleEmailCaptureContainer>
      <HubspotArticleEmailCaptureLeftBackground>
        <LeftBackgroundText>
          <IconsPartyHat width={40} />
          <Heading
            text='FREE BONUS'
            size='3rem'
            weight={700}
            align='center'
            color={palette.white}
          />
        </LeftBackgroundText>
        <LeftBackgroundImage>
          <WaterTextureOverlay opacity={0.62} />
          <TextureOverlay opacity={0.4} />
        </LeftBackgroundImage>
      </HubspotArticleEmailCaptureLeftBackground>
      <HubspotArticleEmailCaptureRightContent>
        {!formResult && (
          <HubspotArticleEmailTextContainer>
            <HubspotArticleEmailDescriptionText>
              {props.descriptionLong.childMarkdownRemark.htmlAst.children.map((child, index) =>
                formatElementFromHtmlAst({
                  element: child,
                  key: `${index}`,
                  template: 'resource',
                })
              )}
            </HubspotArticleEmailDescriptionText>
            <HubspotFormWrapper id={HUBSPOT_ELEM} />
            <ButtonPrimary
              disabled={disabled}
              text={props.ctaText}
              colorScheme='Sunshine'
              customFontWeight={700}
              customBorderRadius={39}
              onClick={handleSubmit}
            />
            <HubspotArticleEmailCtaText>
              {props.ctaCaption.childMarkdownRemark.htmlAst.children.map((child, index) =>
                formatElementFromHtmlAst({
                  element: child,
                  key: `${index}`,
                  template: 'resource',
                })
              )}
            </HubspotArticleEmailCtaText>
          </HubspotArticleEmailTextContainer>
        )}
        {formResult && (
          <HubspotArticleEmailTextContainer>
            <HubspotArticleEmailDescriptionText>
              {props.descriptionLong.childMarkdownRemark.htmlAst.children.map((child, index) =>
                formatElementFromHtmlAst({
                  element: child,
                  key: `${index}`,
                  template: 'resource',
                })
              )}
            </HubspotArticleEmailDescriptionText>
            <HubspotFormResult result={formResult} />
          </HubspotArticleEmailTextContainer>
        )}
      </HubspotArticleEmailCaptureRightContent>
    </HubspotArticleEmailCaptureContainer>
  );
};

export default HubspotArticleEmailCapture;
